/**
 * WhatsNewDrawer — V2 first-login slide-in panel.
 *
 * W43.7 fix (2026-05-31): Suppressed on the /galaxy route (and any canvas-only
 * 3D pages) because the full-width drawer blocks all pan/zoom/floor controls on
 * mobile (390px viewport). On those routes the drawer never auto-shows.
 * The localStorage key has been bumped to "attilius-whats-new-V2b-seen" so
 * users who dismissed the old drawer aren't forced to see it again.
 *
 * Shows once per browser after deploy using localStorage key.
 * Slide-in from right, 400px wide, full viewport height.
 */
import { useEffect, useState } from "react";
import { tokens } from "./operator/tokens";
import { getSession } from "@/lib/session";

const LS_KEY = "attilius-whats-new-V2b-seen";

/** Routes where the drawer must never auto-appear (3D canvas pages). */
const SUPPRESSED_HASH_PREFIXES = ["/galaxy", "/tower", "/pipeline-tower", "/dna"];

const ITEMS = [
  {
    title: "User management",
    desc: "Managers can now add, deactivate, and change roles for team members (Settings → Users)",
  },
  {
    title: "Audit log viewer",
    desc: "Full activity, push, and edit history across the platform (Admin → Audit Log)",
  },
  {
    title: "Company settings",
    desc: "Edit company name, branding, and tenant config (Settings → Company)",
  },
  {
    title: "CSV exports",
    desc: "Export accounts and pipeline data to CSV from any list view",
  },
  {
    title: "Server-backed notification preferences",
    desc: "Your notification settings now sync across devices",
  },
  {
    title: "Terms and Privacy pages",
    desc: "Legal pages live at /legal/terms and /legal/privacy",
  },
  {
    title: "Stronger session security",
    desc: "JWT tokens last 12 hours, idle sessions auto-expire after 30 minutes",
  },
  {
    title: "Error monitoring",
    desc: "Every error is now captured automatically. Bryan Short sees it before you have to report it",
  },
];

export default function WhatsNewDrawer() {
  const [visible, setVisible] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    // Only show when authenticated and not seen yet
    const session = getSession();
    if (!session) return;

    // W43.7: Never auto-show on 3D canvas routes — the drawer blocks controls
    // on mobile (390px viewport). Check hash-based routing.
    try {
      const hash = window.location.hash.replace(/^#/, "");
      const suppressed = SUPPRESSED_HASH_PREFIXES.some(
        (prefix) => hash === prefix || hash.startsWith(prefix + "/") || hash.startsWith(prefix + "?")
      );
      if (suppressed) return;
    } catch {
      // ignore — if we can't read location, allow the normal flow
    }

    try {
      const seen = localStorage.getItem(LS_KEY);
      if (seen === "true") return;
    } catch {
      return;
    }

    // Short delay so the page loads before drawer appears
    const t = setTimeout(() => {
      setMounted(true);
      requestAnimationFrame(() => setVisible(true));
    }, 600);

    return () => clearTimeout(t);
  }, []);

  function dismiss() {
    setVisible(false);
    try {
      localStorage.setItem(LS_KEY, "true");
    } catch {
      // ignore
    }
    // Unmount after transition completes
    setTimeout(() => setMounted(false), 300);
  }

  if (!mounted) return null;

  return (
    <>
      {/* Dimmed backdrop */}
      <div
        aria-hidden="true"
        onClick={dismiss}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.4)",
          zIndex: 998,
          opacity: visible ? 1 : 0,
          transition: "opacity 250ms ease-out",
        }}
      />

      {/* Drawer panel */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="What's new in V2"
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: 400,
          background: "#FFFFFF",
          zIndex: 999,
          display: "flex",
          flexDirection: "column",
          transform: visible ? "translateX(0)" : "translateX(100%)",
          transition: "transform 250ms ease-out",
          overflowY: "auto",
        }}
      >
        {/* Header strip */}
        <div
          style={{
            background: "#0B0B0F",
            padding: "20px 20px 18px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            flexShrink: 0,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <img
              src="/brand/attilius-elephant.png"
              alt="Attilius"
              style={{ width: 28, height: "auto", filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.5))" }}
            />
            <div>
              <div
                style={{
                  fontFamily: tokens.font.display,
                  fontWeight: 700,
                  fontSize: 16,
                  color: "#FAFAF7",
                  letterSpacing: -0.2,
                }}
              >
                What's new in V2
              </div>
              <div
                style={{
                  fontFamily: tokens.font.body,
                  fontSize: 11,
                  color: "#6B6B70",
                  marginTop: 2,
                }}
              >
                May 16, 2026
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={dismiss}
            aria-label="Close"
            style={{
              background: "transparent",
              border: "none",
              color: "#8E8E94",
              cursor: "pointer",
              padding: 6,
              borderRadius: 4,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              lineHeight: 1,
              fontSize: 18,
            }}
          >
            &#x2715;
          </button>
        </div>

        {/* Items */}
        <div style={{ flex: 1, overflowY: "auto" }}>
          {ITEMS.map((item) => (
            <div
              key={item.title}
              style={{
                padding: 16,
                borderBottom: `1px solid ${tokens.color.borderSoft}`,
                display: "flex",
                alignItems: "flex-start",
                gap: 10,
                cursor: "default",
                transition: "background 150ms",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLDivElement).style.background = "#FAFAF7";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLDivElement).style.background = "transparent";
              }}
            >
              {/* Red dot bullet */}
              <div
                aria-hidden="true"
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: "#DC2626",
                  flexShrink: 0,
                  marginTop: 5,
                }}
              />
              <div>
                <div
                  style={{
                    fontFamily: tokens.font.display,
                    fontWeight: 700,
                    fontSize: 13,
                    color: tokens.color.textPrimary,
                    marginBottom: 3,
                  }}
                >
                  {item.title}
                </div>
                <div
                  style={{
                    fontFamily: tokens.font.body,
                    fontSize: 13,
                    color: tokens.color.textMuted,
                    lineHeight: 1.5,
                  }}
                >
                  {item.desc}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: "16px 20px",
            borderTop: `1px solid ${tokens.color.border}`,
            flexShrink: 0,
          }}
        >
          <button
            type="button"
            onClick={dismiss}
            style={{
              width: "100%",
              height: 38,
              background: tokens.color.red,
              color: "#FFFFFF",
              border: "none",
              borderRadius: 6,
              fontFamily: tokens.font.body,
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
              marginBottom: 12,
            }}
          >
            Got it
          </button>
          <div
            style={{
              fontFamily: tokens.font.body,
              fontSize: 12,
              color: tokens.color.textSubtle,
              textAlign: "center",
            }}
          >
            Questions? Email{" "}
            <a
              href="mailto:support@mi6group.com"
              style={{ color: tokens.color.textMuted, textDecoration: "underline" }}
            >
              support@mi6group.com
            </a>
          </div>
        </div>
      </div>
    </>
  );
}
